import { useCallback, useEffect, useRef, useState } from 'react';
import { staticDefaults } from '@/config/static/catalog';
import { bulkCsvPanelProcessingMs } from '@/config/static/catalog/bulk';
import type { BulkUserImportDraft } from '@/types/bulkUserImport';
import {
  peekConversationAttachmentMeta,
  peekConversationAttachmentText,
  setConversationAttachmentText,
  storeConversationAttachment,
} from '@/utils/conversationAttachment';
import { parseBulkUserCsv } from '@/utils/parseBulkUserCsv';
import { serializeBulkUserCsv } from '@/utils/serializeBulkUserCsv';
import {
  buildBulkIssueAllMobileUserMessage,
  buildBulkIssueSingleMobileUserMessage,
  countMobileCodeSentRows,
  getBulkUserPostImportStatus,
} from '@/utils/bulkUserPostImport';
import type { BulkCredentialIssuePhase } from '@/components/bulk-import/BulkCredentialIssueFollowUp';
import { getBulkImportRightPanelMode } from '@/components/bulk-import/getBulkImportRightPanelMode';
import type { WorkflowUiPhase } from '@/components/workflow/types';
import type { WorkflowProgressStep } from '@/components/workflow/types';

type FlowDraft = Record<string, unknown>;

export interface UseBulkUserImportPanelOptions {
  enabled: boolean;
  conversationId: string | undefined;
  uiPhase: WorkflowUiPhase;
  progressRail: WorkflowProgressStep[];
  bulkDraft: BulkUserImportDraft | null;
  actorEmail: string;
  updateDraft: (patch: Partial<FlowDraft>) => void;
  appendUserActionMessage: (content: string, messageId?: string) => string | null;
}

function resolveBulkDraftForPanel(
  bulkDraft: BulkUserImportDraft | null,
  conversationId: string | undefined
): BulkUserImportDraft | null {
  if (!bulkDraft || !conversationId) return bulkDraft;
  if (bulkDraft.hasCsvAtSend) return bulkDraft;
  const meta = peekConversationAttachmentMeta(conversationId);
  const text = peekConversationAttachmentText(conversationId);
  if (meta && text) {
    return {
      ...bulkDraft,
      hasCsvAtSend: true,
      csvFileName: bulkDraft.csvFileName ?? meta.fileName,
    };
  }
  return bulkDraft;
}

export function useBulkUserImportPanel({
  enabled,
  conversationId,
  uiPhase,
  progressRail,
  bulkDraft,
  actorEmail,
  updateDraft,
  appendUserActionMessage,
}: UseBulkUserImportPanelOptions) {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [credentialIssuesByMessageId, setCredentialIssuesByMessageId] = useState<
    Record<string, { phase: BulkCredentialIssuePhase; count: number }>
  >({});
  const [credentialToastCount, setCredentialToastCount] = useState<number | null>(null);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [csvProcessing, setCsvProcessing] = useState(false);

  const bulkDraftRef = useRef<BulkUserImportDraft | null>(null);
  const uploadGenRef = useRef(0);
  const uploadTimerRef = useRef<number | null>(null);
  const credentialIssueTimersRef = useRef<number[]>([]);

  bulkDraftRef.current = bulkDraft;
  const draftForPanel = resolveBulkDraftForPanel(bulkDraft, conversationId);

  const credentialIssuePhase =
    Object.values(credentialIssuesByMessageId).find(
      (issue) => issue.phase === 'preparing' || issue.phase === 'issuing'
    )?.phase ?? 'idle';

  useEffect(
    () => () => {
      credentialIssueTimersRef.current.forEach((id) => window.clearTimeout(id));
    },
    []
  );

  useEffect(() => {
    if (!enabled) return;
    setRightPanelOpen(true);
    setCsvProcessing(false);
    setCredentialIssuesByMessageId({});
    setCredentialToastCount(null);
    credentialIssueTimersRef.current.forEach((id) => window.clearTimeout(id));
    credentialIssueTimersRef.current = [];
    uploadGenRef.current += 1;
    if (uploadTimerRef.current != null) {
      window.clearTimeout(uploadTimerRef.current);
      uploadTimerRef.current = null;
    }
  }, [conversationId, enabled]);

  const handleUpload = useCallback(
    async (file: File) => {
      if (!conversationId) return;
      setUploadError(null);

      const gen = ++uploadGenRef.current;
      setCsvProcessing(true);

      updateDraft({
        rows: [],
        csvFileName: file.name,
        hasCsvAtSend: true,
      });

      try {
        const text = await file.text();
        await storeConversationAttachment(conversationId, file);

        await new Promise<void>((resolve) => {
          if (uploadTimerRef.current != null) {
            window.clearTimeout(uploadTimerRef.current);
          }
          uploadTimerRef.current = window.setTimeout(() => {
            uploadTimerRef.current = null;
            resolve();
          }, bulkCsvPanelProcessingMs);
        });

        if (gen !== uploadGenRef.current) return;

        const rows = parseBulkUserCsv(text, actorEmail);
        updateDraft({
          rows,
          csvFileName: file.name,
          hasCsvAtSend: true,
        });
      } catch {
        if (gen === uploadGenRef.current) {
          setUploadError('Could not read the CSV file.');
        }
      } finally {
        if (gen === uploadGenRef.current) {
          setCsvProcessing(false);
        }
      }
    },
    [actorEmail, conversationId, updateDraft]
  );

  const handleRowUpdate = useCallback(
    (rowId: string, patch: Partial<BulkUserImportDraft['rows'][number]>) => {
      if (!bulkDraft) return;
      const rows = bulkDraft.rows.map((row) => (row.id === rowId ? { ...row, ...patch } : row));
      updateDraft({ rows });
      if (conversationId) {
        setConversationAttachmentText(conversationId, serializeBulkUserCsv(rows));
      }
    },
    [bulkDraft, conversationId, updateDraft]
  );

  const startCredentialIssue = useCallback(
    ({ mode, rowId }: { mode: 'all' | 'single'; rowId?: string }) => {
      if (credentialIssuePhase === 'preparing' || credentialIssuePhase === 'issuing') return;

      const draft = bulkDraftRef.current;
      if (!draft) return;

      let count = 0;
      let targetRow: BulkUserImportDraft['rows'][number] | undefined;
      let messageId: string | null = null;

      if (mode === 'single') {
        targetRow = draft.rows.find((row) => row.id === rowId);
        if (!targetRow || getBulkUserPostImportStatus(targetRow) !== 'code-sent') return;
        count = 1;
        messageId = appendUserActionMessage(
          buildBulkIssueSingleMobileUserMessage(targetRow.firstName, targetRow.lastName)
        );
      } else {
        count = countMobileCodeSentRows(draft.rows);
        if (count === 0) return;
        messageId = appendUserActionMessage(buildBulkIssueAllMobileUserMessage(count));
      }

      if (!messageId) return;

      credentialIssueTimersRef.current.forEach((id) => window.clearTimeout(id));
      credentialIssueTimersRef.current = [];

      setCredentialIssuesByMessageId((prev) => ({
        ...prev,
        [messageId!]: { phase: 'preparing', count },
      }));
      setCredentialToastCount(null);

      const { prepMs, issuingMs } = staticDefaults.bulk.credentialIssueTiming;

      credentialIssueTimersRef.current.push(
        window.setTimeout(() => {
          setCredentialIssuesByMessageId((prev) => {
            const current = prev[messageId!];
            if (!current) return prev;
            return { ...prev, [messageId!]: { ...current, phase: 'issuing' } };
          });
        }, prepMs)
      );

      credentialIssueTimersRef.current.push(
        window.setTimeout(() => {
          const currentDraft = bulkDraftRef.current;
          if (!currentDraft) return;
          const rows = currentDraft.rows.map((row) => {
            if (mode === 'single') {
              return row.id === rowId ? { ...row, mobileCredentialIssued: true } : row;
            }
            return getBulkUserPostImportStatus(row) === 'code-sent'
              ? { ...row, mobileCredentialIssued: true }
              : row;
          });
          updateDraft({ rows });
          setCredentialIssuesByMessageId((prev) => {
            const current = prev[messageId!];
            if (!current) return prev;
            return { ...prev, [messageId!]: { phase: 'complete', count: current.count } };
          });
          setCredentialToastCount(count);
        }, prepMs + issuingMs)
      );
    },
    [appendUserActionMessage, credentialIssuePhase, updateDraft]
  );

  const rightPanelMode =
    enabled && draftForPanel
      ? getBulkImportRightPanelMode(uiPhase, draftForPanel, progressRail, csvProcessing)
      : 'hidden';

  const panelAvailable = enabled && rightPanelMode !== 'hidden';
  const splitLayoutOpen = panelAvailable && rightPanelOpen;
  const tableReadOnly = uiPhase === 'saving' || uiPhase === 'success';

  const noCsvUploadVisible =
    enabled &&
    draftForPanel != null &&
    !draftForPanel.hasCsvAtSend &&
    draftForPanel.rows.length === 0 &&
    (rightPanelMode === 'empty-upload' ||
      rightPanelMode === 'upload-skeleton' ||
      rightPanelMode === 'skeleton');

  return {
    uploadError,
    setUploadError,
    credentialIssuesByMessageId,
    credentialIssuePhase,
    credentialToastCount,
    setCredentialToastCount,
    rightPanelOpen,
    setRightPanelOpen,
    csvProcessing,
    draftForPanel,
    rightPanelMode,
    panelAvailable,
    splitLayoutOpen,
    tableReadOnly,
    noCsvUploadVisible,
    handleUpload,
    handleRowUpdate,
    handleIssueAllMobile: () => startCredentialIssue({ mode: 'all' }),
    handleIssueSingleMobile: (row: BulkUserImportDraft['rows'][number]) =>
      startCredentialIssue({ mode: 'single', rowId: row.id }),
  };
}
